import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { forumApi } from "../api/forumApi";
import { useAuth } from "../auth/authStore";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { getSiteOrigin } from "../utils/seo";
import ReactionParticlesLayer from "../ui/reactions/ReactionParticlesLayer";
import {
  DEFAULT_REACTION_KIND,
  REACTION_OPTIONS,
  normalizeReactionKind,
} from "../ui/reactions/reactionKinds";
import { getReactionBurstPayload } from "../ui/reactions/reactionModel";
import { useReactionParticles } from "../ui/reactions/useReactionParticles";
import { useWs } from "../ws/wsStore";
import { buildPsychologistProfilePath } from "../utils/psychologistProfile";
import ReportContentButton from "../components/reports/ReportContentButton";

const TOPICS_PAGE_SIZE = 20;
const MY_CONTENT_PAGE_SIZE = 20;
const TOPIC_FILTERS = {
  ALL: "all",
  ACTIVE: "active",
  MINE: "mine",
  UNANSWERED: "unanswered",
};
const COMPOSER_EMOJIS = REACTION_OPTIONS.map((item) => item.emoji);
const TOPIC_SLUG_MAX_LEN = 80;
const TRANSLIT_MAP = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ё: "yo",
  ж: "zh",
  з: "z",
  и: "i",
  й: "j",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "kh",
  ц: "ts",
  ч: "ch",
  ш: "sh",
  щ: "shch",
  ы: "y",
  э: "e",
  ю: "yu",
  я: "ya",
  ь: "",
  ъ: "",
};
const FORUM_WS_CHANNELS = {
  root: "/topic/forum",
  sectionsPrefix: "/topic/forum/sections/",
  topicsPrefix: "/topic/forum/topics/",
  notifications: "/user/queue/notifications",
};
const FORUM_NOTIFICATION_ACTIONS = new Set([
  "FORUM_COMMENT_NEW",
  "FORUM_REACTION_NEW",
  "COMMENT_REPLY",
]);
const FORUM_SELF_HANDLED_REALTIME_TYPES = new Set([
  "FORUM_TOPIC_REACTION_TOGGLED",
  "FORUM_COMMENT_REACTION_TOGGLED",
  "FORUM_COMMENT_CREATED",
]);
const FORUM_TOPIC_LINK_RE = /\/forum\/topics\/(\d+)/i;

function parseId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function toUserKey(value) {
  if (value === undefined || value === null) return "";
  const key = String(value).trim();
  return key || "";
}

function slugifyTopic(value = "") {
  const transliterated = String(value || "")
    .trim()
    .toLowerCase()
    .split("")
    .map((char) => TRANSLIT_MAP[char] ?? char)
    .join("");

  return transliterated
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, TOPIC_SLUG_MAX_LEN);
}

function parseTopicId(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const match = raw.match(/^(\d+)/);
  return parseId(match ? match[1] : raw);
}

function parseTopicIdFromUrl(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const match = raw.match(FORUM_TOPIC_LINK_RE);
  return parseId(match ? match[1] : null);
}

function extractForumRealtimeEvent(payload) {
  if (!payload || typeof payload !== "object") return null;

  const dto =
    payload.notification && typeof payload.notification === "object"
      ? payload.notification
      : payload;
  const dtoPayload = dto.payload && typeof dto.payload === "object" ? dto.payload : {};
  const rawType = String(
    dtoPayload.action ||
      payload.action ||
      dto.action ||
      payload.eventType ||
      dto.eventType ||
      payload.type ||
      dto.type ||
      ""
  )
    .trim()
    .toUpperCase();

  const topicId = parseTopicId(
    dtoPayload.topicId ??
      dto.topicId ??
      dtoPayload.topic?.id ??
      dto.topic?.id ??
      parseTopicIdFromUrl(dtoPayload.linkUrl || dto.linkUrl || payload.linkUrl || "")
  );
  const sectionId = parseId(
    dtoPayload.sectionId ??
      dto.sectionId ??
      dtoPayload.section?.id ??
      dto.section?.id ??
      dtoPayload.topic?.sectionId ??
      dto.topic?.sectionId
  );
  const commentId = parseId(
    dtoPayload.commentId ??
      dto.commentId ??
      dtoPayload.comment?.id ??
      dto.comment?.id
  );
  const actorCandidate =
    dtoPayload.actorUserId ??
    dto.actorUserId ??
    dtoPayload.byUserId ??
    dto.byUserId ??
    dtoPayload.actor?.id ??
    dto.actor?.id ??
    dtoPayload.userId ??
    dto.userId;
  const actorUserId = parseId(actorCandidate);
  const actorUserKey = actorUserId ? String(actorUserId) : toUserKey(actorCandidate);

  const isForumAction =
    rawType.startsWith("FORUM_") || FORUM_NOTIFICATION_ACTIONS.has(rawType);
  if (!isForumAction && !topicId && !sectionId && !commentId) return null;

  return {
    type: rawType || "FORUM_UPDATED",
    sectionId,
    topicId,
    commentId,
    actorUserId,
    actorUserKey,
  };
}

function buildTopicPath(topicId, topicTitle = "") {
  const id = parseId(topicId);
  if (!id) return "/forum";
  const slug = slugifyTopic(topicTitle);
  return `/forum/topics/${id}${slug ? `-${slug}` : ""}`;
}

function initials(name = "") {
  const words = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (!words.length) return "А";
  return words.map((x) => x[0]).join("").toUpperCase();
}

function avatarTone(seed = "") {
  const palette = [
    "var(--lav-dim)",
    "var(--mint-dim)",
    "rgba(245,184,31,0.15)",
    "rgba(231,156,189,0.2)",
    "rgba(203,203,203,0.3)",
  ];
  const text = String(seed || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) % 2147483647;
  return palette[Math.abs(hash) % palette.length];
}

function formatDateTime(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function toTimestamp(value) {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
}

function formatRelativeTime(iso) {
  const ts = toTimestamp(iso);
  if (!ts) return "";
  const now = Date.now();
  const diffMs = Math.max(0, now - ts);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < hour) {
    const mins = Math.max(1, Math.round(diffMs / minute));
    return `${mins} мин назад`;
  }
  if (diffMs < day) {
    const hours = Math.max(1, Math.round(diffMs / hour));
    return `${hours} ч назад`;
  }
  if (diffMs < day * 2) return "вчера";
  const days = Math.max(2, Math.round(diffMs / day));
  if (days <= 7) return `${days} дн назад`;
  return formatDate(iso);
}

function mapForumError(error, fallback = "Не удалось выполнить действие") {
  const status = Number(error?.status || 0);
  const raw = String(error?.message || error?.error || "").trim();

  if (raw.includes("FORUM_TOPIC_LOCKED")) return "Тема закрыта. Новые комментарии не принимаются.";
  if (raw.includes("FORUM_ANONYMOUS_TOPICS_DISABLED")) return "В этом разделе анонимные темы отключены.";
  if (raw.includes("FORUM_ANONYMOUS_COMMENTS_DISABLED")) return "В этом разделе анонимные комментарии отключены.";
  if (raw.includes("FORUM_SECTION_NOT_FOUND")) return "Раздел форума не найден.";
  if (raw.includes("FORUM_TOPIC_NOT_FOUND")) return "Тема не найдена.";

  if (status === 401 || status === 403) return "Нужно авторизоваться, чтобы выполнить это действие.";
  if (status === 409) return "Действие сейчас недоступно из-за состояния темы.";

  return raw || fallback;
}

function buildCommentsTree(items = []) {
  const byId = new Map();
  items.forEach((item) => byId.set(item.id, { ...item, children: [] }));

  const roots = [];
  items.forEach((item) => {
    const node = byId.get(item.id);
    if (!node) return;
    if (item.parentCommentId && byId.has(item.parentCommentId)) {
      byId.get(item.parentCommentId).children.push(node);
      return;
    }
    roots.push(node);
  });

  return roots;
}

function paginate(totalPages, current) {
  if (totalPages <= 1) return [1];
  const page = Math.max(1, Math.min(current, totalPages));
  const pages = new Set([1, totalPages, page - 1, page, page + 1]);
  const out = Array.from(pages)
    .filter((x) => x >= 1 && x <= totalPages)
    .sort((a, b) => a - b);
  const final = [];
  out.forEach((value, index) => {
    if (index > 0 && value - out[index - 1] > 1) final.push("...");
    final.push(value);
  });
  return final;
}

function Avatar({ author, size = "md" }) {
  const name = author?.name || "Анонимно";
  const bg = avatarTone(`${author?.id || 0}-${name}`);
  const image = String(author?.avatarUrl || "");
  return (
    <div className={`forum-avatar forum-avatar-${size}`} style={{ background: bg }}>
      {image ? <img src={image} alt={name} /> : initials(name)}
    </div>
  );
}

function ReactionSelector({
  reactionsCount = 0,
  myReactionKind = null,
  compact = false,
  onToggle,
}) {
  const activeKind = normalizeReactionKind(myReactionKind, null);
  return (
    <div className={`forum-reaction-picker${compact ? " compact" : ""}`}>
      <div className="forum-reaction-picker-items">
        {REACTION_OPTIONS.map((item) => {
          const active = activeKind === item.kind;
          return (
            <button
              key={item.kind}
              type="button"
              className={`forum-reaction-btn${compact ? " compact" : ""}${active ? " active" : ""}`}
              title={item.label}
              aria-label={item.label}
              onClick={(event) => onToggle?.(event, item.kind)}
            >
              {item.emoji}
            </button>
          );
        })}
      </div>
      <span className="forum-reaction-total">{Math.max(0, Number(reactionsCount) || 0)}</span>
    </div>
  );
}

export default function Forum() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { sectionId: sectionIdParam, topicId: topicIdParam, meTab: meTabParam } = useParams();
  const { isAuthenticated, role, me, booting } = useAuth();
  const { subscribe: wsSubscribe } = useWs();

  const sectionId = parseId(sectionIdParam);
  const topicId = parseTopicId(topicIdParam);
  const meTab = String(meTabParam || "").toLowerCase();
  const view = topicId
    ? "topic"
    : meTab === "topics"
    ? "my-topics"
    : meTab === "comments"
    ? "my-comments"
    : sectionId
    ? "topics"
    : "sections";
  const canModerate = role === "ADMIN" || role === "MODERATOR";
  const meId = parseId(me?.id);
  const meUserKey = React.useMemo(() => toUserKey(me?.id), [me?.id]);

  const [sections, setSections] = React.useState([]);
  const [sectionsLoading, setSectionsLoading] = React.useState(true);
  const [sectionsError, setSectionsError] = React.useState("");

  const [topicsPage, setTopicsPage] = React.useState({
    content: [],
    number: 0,
    size: TOPICS_PAGE_SIZE,
    totalElements: 0,
    totalPages: 1,
    first: true,
    last: true,
    empty: true,
  });
  const [topicsLoading, setTopicsLoading] = React.useState(false);
  const [topicsError, setTopicsError] = React.useState("");

  const [myTopicsPage, setMyTopicsPage] = React.useState({
    content: [],
    number: 0,
    size: MY_CONTENT_PAGE_SIZE,
    totalElements: 0,
    totalPages: 1,
    first: true,
    last: true,
    empty: true,
  });
  const [myTopicsLoading, setMyTopicsLoading] = React.useState(false);
  const [myTopicsError, setMyTopicsError] = React.useState("");

  const [myCommentsPage, setMyCommentsPage] = React.useState({
    content: [],
    number: 0,
    size: MY_CONTENT_PAGE_SIZE,
    totalElements: 0,
    totalPages: 1,
    first: true,
    last: true,
    empty: true,
  });
  const [myCommentsLoading, setMyCommentsLoading] = React.useState(false);
  const [myCommentsError, setMyCommentsError] = React.useState("");

  const [topic, setTopic] = React.useState(null);
  const [topicLoading, setTopicLoading] = React.useState(false);
  const [topicError, setTopicError] = React.useState("");

  const [comments, setComments] = React.useState([]);
  const [commentsLoading, setCommentsLoading] = React.useState(false);
  const [commentsError, setCommentsError] = React.useState("");

  const [relatedTopics, setRelatedTopics] = React.useState([]);
  const [activeTopics, setActiveTopics] = React.useState([]);
  const [sectionLatestTopics, setSectionLatestTopics] = React.useState({});
  const [activeTopicsLoading, setActiveTopicsLoading] = React.useState(false);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [topicsFilter, setTopicsFilter] = React.useState(TOPIC_FILTERS.ALL);

  const [modalOpen, setModalOpen] = React.useState(false);
  const [createPending, setCreatePending] = React.useState(false);
  const [createForm, setCreateForm] = React.useState({
    sectionId: "",
    title: "",
    content: "",
    anonymous: false,
  });

  const [commentText, setCommentText] = React.useState("");
  const [commentAnonymous, setCommentAnonymous] = React.useState(false);
  const [commentPending, setCommentPending] = React.useState(false);

  const [replyToCommentId, setReplyToCommentId] = React.useState(null);
  const [replyText, setReplyText] = React.useState("");
  const [replyAnonymous, setReplyAnonymous] = React.useState(false);
  const [replyPending, setReplyPending] = React.useState(false);

  const [notice, setNotice] = React.useState({ type: "", text: "" });
  const noticeTimerRef = React.useRef(null);
  const realtimeReloadTimerRef = React.useRef(null);
  const realtimeEventDedupRef = React.useRef({ key: "", at: 0 });
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const {
    particles: reactionParticles,
    emitBurst: emitReactionBurst,
    getBurstOriginFromEvent: getReactionBurstOriginFromEvent,
  } = useReactionParticles({
    particleCount: 14,
    floaterCount: 6,
    dotCount: 16,
    rippleCount: 3,
    minDistance: 28,
    maxDistance: 84,
    minSize: 15,
    maxSize: 26,
    minDuration: 700,
    maxDuration: 1120,
    minDotSize: 5,
    maxDotSize: 9,
    minDotDuration: 360,
    maxDotDuration: 700,
    rippleLifeMs: 640,
  });

  const origin = React.useMemo(() => getSiteOrigin(), []);

  const notify = React.useCallback((type, text) => {
    setNotice({ type, text });
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => {
      setNotice({ type: "", text: "" });
    }, 2800);
  }, []);

  const appendEmoji = React.useCallback((setText, emoji) => {
    setText((prev) => {
      const raw = String(prev || "");
      const trimmed = raw.replace(/\s+$/, "");
      const next = trimmed ? `${trimmed} ${emoji}` : emoji;
      return `${next} `;
    });
  }, []);

  React.useEffect(() => {
    return () => {
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
      if (realtimeReloadTimerRef.current) clearTimeout(realtimeReloadTimerRef.current);
    };
  }, []);

  const redirectToLogin = React.useCallback(() => {
    navigate("/login", {
      state: { from: location.pathname + location.search },
    });
  }, [location.pathname, location.search, navigate]);

  const openPsychologistProfile = React.useCallback(
    (event, authorId) => {
      const path = buildPsychologistProfilePath(authorId);
      if (!path) return;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      navigate(path);
    },
    [navigate]
  );

  const renderAuthorName = React.useCallback(
    (author, className = "") => {
      const label = author?.name || "Анонимно";
      if (!author?.id) return <span className={className}>{label}</span>;
      return (
        <button
          type="button"
          className={`${className} b-psych-link-btn`.trim()}
          onClick={(event) => openPsychologistProfile(event, author.id)}
        >
          {label}
        </button>
      );
    },
    [openPsychologistProfile]
  );

  const requireAuth = React.useCallback(
    (fallbackMessage = "Для этого действия нужна авторизация.") => {
      if (isAuthenticated) return true;
      notify("warning", fallbackMessage);
      redirectToLogin();
      return false;
    },
    [isAuthenticated, notify, redirectToLogin]
  );

  const loadSections = React.useCallback(async () => {
    setSectionsLoading(true);
    setSectionsError("");
    try {
      const list = await forumApi.listSections();
      setSections(list);
    } catch (error) {
      console.error(error);
      setSections([]);
      setSectionsError(mapForumError(error, "Не удалось загрузить разделы форума."));
    } finally {
      setSectionsLoading(false);
    }
  }, []);

  const loadTopics = React.useCallback(async (targetSectionId, page = 0) => {
    if (!targetSectionId) {
      setTopicsPage((prev) => ({ ...prev, content: [] }));
      return;
    }
    setTopicsLoading(true);
    setTopicsError("");
    try {
      const pageData = await forumApi.listTopics({
        sectionId: targetSectionId,
        page,
        size: TOPICS_PAGE_SIZE,
      });
      setTopicsPage(pageData);
    } catch (error) {
      console.error(error);
      setTopicsError(mapForumError(error, "Не удалось загрузить темы раздела."));
      setTopicsPage({
        content: [],
        number: 0,
        size: TOPICS_PAGE_SIZE,
        totalElements: 0,
        totalPages: 1,
        first: true,
        last: true,
        empty: true,
      });
    } finally {
      setTopicsLoading(false);
    }
  }, []);

  const loadTopic = React.useCallback(async (targetTopicId, incrementView = true) => {
    if (!targetTopicId) return;
    setTopicLoading(true);
    setTopicError("");
    try {
      const data = await forumApi.getTopic(targetTopicId, { incrementView });
      setTopic(data);
    } catch (error) {
      console.error(error);
      setTopic(null);
      setTopicError(mapForumError(error, "Не удалось загрузить тему."));
    } finally {
      setTopicLoading(false);
    }
  }, []);

  const loadComments = React.useCallback(async (targetTopicId) => {
    if (!targetTopicId) return;
    setCommentsLoading(true);
    setCommentsError("");
    try {
      const list = await forumApi.listComments(targetTopicId);
      setComments(list);
    } catch (error) {
      console.error(error);
      setComments([]);
      setCommentsError(mapForumError(error, "Не удалось загрузить комментарии."));
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  const loadMyTopics = React.useCallback(async (page = 0) => {
    setMyTopicsLoading(true);
    setMyTopicsError("");
    try {
      const data = await forumApi.listMyTopics({
        page,
        size: MY_CONTENT_PAGE_SIZE,
      });
      setMyTopicsPage(data);
    } catch (error) {
      console.error(error);
      setMyTopicsError(mapForumError(error, "Не удалось загрузить ваши темы."));
      setMyTopicsPage({
        content: [],
        number: 0,
        size: MY_CONTENT_PAGE_SIZE,
        totalElements: 0,
        totalPages: 1,
        first: true,
        last: true,
        empty: true,
      });
    } finally {
      setMyTopicsLoading(false);
    }
  }, []);

  const loadMyComments = React.useCallback(async (page = 0) => {
    setMyCommentsLoading(true);
    setMyCommentsError("");
    try {
      const data = await forumApi.listMyComments({
        page,
        size: MY_CONTENT_PAGE_SIZE,
      });
      setMyCommentsPage(data);
    } catch (error) {
      console.error(error);
      setMyCommentsError(mapForumError(error, "Не удалось загрузить ваши комментарии."));
      setMyCommentsPage({
        content: [],
        number: 0,
        size: MY_CONTENT_PAGE_SIZE,
        totalElements: 0,
        totalPages: 1,
        first: true,
        last: true,
        empty: true,
      });
    } finally {
      setMyCommentsLoading(false);
    }
  }, []);

  const loadActiveTopics = React.useCallback(async (sectionList = []) => {
    const ids = sectionList.map((item) => item?.id).filter(Boolean);
    if (!ids.length) {
      setActiveTopics([]);
      setSectionLatestTopics({});
      return;
    }
    setActiveTopicsLoading(true);
    try {
      const results = await Promise.allSettled(
        ids.map(async (id) => {
          const pageData = await forumApi.listTopics({ sectionId: id, page: 0, size: 8 });
          return { sectionId: id, pageData };
        })
      );
      const map = new Map();
      const latestBySection = {};
      results.forEach((res) => {
        if (res.status !== "fulfilled") return;
        const content = Array.isArray(res.value?.pageData?.content) ? res.value.pageData.content : [];
        content.forEach((topicItem) => {
          if (!map.has(topicItem.id)) {
            map.set(topicItem.id, topicItem);
          }
        });
        const latest = [...content].sort(
          (a, b) => toTimestamp(b.lastModified || b.createdWhen) - toTimestamp(a.lastModified || a.createdWhen)
        )[0];
        if (latest && res.value?.sectionId) {
          latestBySection[res.value.sectionId] = latest;
        }
      });
      const next = Array.from(map.values())
        .sort(
          (a, b) =>
            b.commentsCount +
            b.reactionsCount +
            b.viewsCount -
            (a.commentsCount + a.reactionsCount + a.viewsCount)
        )
        .slice(0, 10);
      setActiveTopics(next);
      setSectionLatestTopics(latestBySection);
    } catch (error) {
      setActiveTopics([]);
      setSectionLatestTopics({});
    } finally {
      setActiveTopicsLoading(false);
    }
  }, []);

  const refreshCurrentViewRealtime = React.useCallback(() => {
    if (view === "sections") {
      loadSections();
      return;
    }
    if (view === "topics" && sectionId) {
      loadTopics(sectionId, topicsPage.number || 0);
      return;
    }
    if (view === "topic" && topicId) {
      loadTopic(topicId, false);
      loadComments(topicId);
      return;
    }
    if (view === "my-topics" && isAuthenticated) {
      loadMyTopics(myTopicsPage.number || 0);
      return;
    }
    if (view === "my-comments" && isAuthenticated) {
      loadMyComments(myCommentsPage.number || 0);
    }
  }, [
    isAuthenticated,
    loadComments,
    loadMyComments,
    loadMyTopics,
    loadSections,
    loadTopic,
    loadTopics,
    myCommentsPage.number,
    myTopicsPage.number,
    sectionId,
    topicId,
    topicsPage.number,
    view,
  ]);

  const isRealtimeEventRelevant = React.useCallback(
    (eventMeta) => {
      if (!eventMeta) return false;
      const sameActorById = meId && eventMeta.actorUserId && eventMeta.actorUserId === meId;
      const sameActorByKey = meUserKey && eventMeta.actorUserKey && eventMeta.actorUserKey === meUserKey;
      if ((sameActorById || sameActorByKey) && FORUM_SELF_HANDLED_REALTIME_TYPES.has(eventMeta.type)) {
        return false;
      }
      if (view === "sections" || view === "my-topics" || view === "my-comments") return true;
      if (view === "topics") {
        if (!sectionId) return true;
        if (eventMeta.sectionId && eventMeta.sectionId !== sectionId) return false;
        return true;
      }
      if (view === "topic") {
        if (!topicId) return false;
        if (eventMeta.topicId && eventMeta.topicId !== topicId) return false;
        if (eventMeta.sectionId && topic?.sectionId && eventMeta.sectionId !== topic.sectionId) return false;
        return true;
      }
      return true;
    },
    [meId, meUserKey, sectionId, topic?.sectionId, topicId, view]
  );

  const queueRealtimeRefresh = React.useCallback(
    (eventMeta) => {
      const key = `${eventMeta?.type || ""}:${eventMeta?.sectionId || 0}:${eventMeta?.topicId || 0}:${
        eventMeta?.commentId || 0
      }`;
      const now = Date.now();
      const prev = realtimeEventDedupRef.current;
      if (prev.key === key && now - prev.at < 700) return;
      realtimeEventDedupRef.current = { key, at: now };

      if (realtimeReloadTimerRef.current) return;
      realtimeReloadTimerRef.current = setTimeout(() => {
        realtimeReloadTimerRef.current = null;
        refreshCurrentViewRealtime();
        if ((view === "sections" || view === "topics" || view === "my-topics" || view === "my-comments") && sections.length) {
          loadActiveTopics(sections);
        }
        if (isAuthenticated) {
          if (view === "my-topics") {
            loadMyTopics(myTopicsPage.number || 0);
          } else if (view === "my-comments") {
            loadMyComments(myCommentsPage.number || 0);
          }
        }
      }, 220);
    },
    [
      isAuthenticated,
      loadActiveTopics,
      loadMyComments,
      loadMyTopics,
      myCommentsPage.number,
      myTopicsPage.number,
      refreshCurrentViewRealtime,
      sections,
      view,
    ]
  );

  React.useEffect(() => {
    loadSections();
  }, [loadSections]);

  React.useEffect(() => {
    if (!sections.length) return;
    loadActiveTopics(sections);
  }, [loadActiveTopics, sections]);

  React.useEffect(() => {
    if (booting) return;
    if (view !== "my-topics" && view !== "my-comments") return;
    if (isAuthenticated) return;
    redirectToLogin();
  }, [booting, isAuthenticated, redirectToLogin, view]);

  React.useEffect(() => {
    if (!sections.length) return;
    setCreateForm((prev) => {
      if (prev.sectionId) return prev;
      return { ...prev, sectionId: String(sectionId || sections[0].id || "") };
    });
  }, [sectionId, sections]);

  React.useEffect(() => {
    if (!sectionId || view !== "topics") return;
    loadTopics(sectionId, 0);
  }, [loadTopics, sectionId, view]);

  React.useEffect(() => {
    if (!isAuthenticated || view !== "my-topics") return;
    loadMyTopics(0);
  }, [isAuthenticated, loadMyTopics, view]);

  React.useEffect(() => {
    if (!isAuthenticated || view !== "my-comments") return;
    loadMyComments(0);
  }, [isAuthenticated, loadMyComments, view]);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (view === "my-topics" || view === "my-comments") return;
    loadMyTopics(0);
    loadMyComments(0);
  }, [isAuthenticated, loadMyComments, loadMyTopics, view]);

  React.useEffect(() => {
    if (!topicId || view !== "topic") return;
    loadTopic(topicId, true);
    loadComments(topicId);
  }, [loadComments, loadTopic, topicId, view]);

  React.useEffect(() => {
    if (view !== "topic" || !topic?.sectionId) {
      setRelatedTopics([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const pageData = await forumApi.listTopics({
          sectionId: topic.sectionId,
          page: 0,
          size: 8,
        });
        if (cancelled) return;
        setRelatedTopics(pageData.content.filter((x) => x.id !== topic.id));
      } catch (error) {
        if (!cancelled) {
          setRelatedTopics([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [topic?.id, topic?.sectionId, view]);

  React.useEffect(() => {
    if (!isAuthenticated) return;

    const handleRealtimePayload = (payload) => {
      const eventMeta = extractForumRealtimeEvent(payload);
      if (!eventMeta) return;
      if (!isRealtimeEventRelevant(eventMeta)) return;
      queueRealtimeRefresh(eventMeta);
    };

    const unsubscribers = [wsSubscribe(FORUM_WS_CHANNELS.notifications, handleRealtimePayload)];

    if (view === "sections" || view === "my-topics" || view === "my-comments") {
      unsubscribers.push(wsSubscribe(FORUM_WS_CHANNELS.root, handleRealtimePayload));
    } else if (view === "topics" && sectionId) {
      unsubscribers.push(
        wsSubscribe(`${FORUM_WS_CHANNELS.sectionsPrefix}${sectionId}`, handleRealtimePayload)
      );
    } else if (view === "topic" && topicId) {
      unsubscribers.push(
        wsSubscribe(`${FORUM_WS_CHANNELS.topicsPrefix}${topicId}`, handleRealtimePayload)
      );
    }

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        try {
          unsubscribe?.();
        } catch {}
      });
    };
  }, [
    isAuthenticated,
    isRealtimeEventRelevant,
    queueRealtimeRefresh,
    sectionId,
    topicId,
    view,
    wsSubscribe,
  ]);

  React.useEffect(() => {
    if (view !== "topic" || !topic?.id) return;
    const expectedPath = buildTopicPath(topic.id, topic.title || "");
    if (!expectedPath || location.pathname === expectedPath) return;
    navigate(`${expectedPath}${location.search || ""}${location.hash || ""}`, { replace: true });
  }, [
    location.hash,
    location.pathname,
    location.search,
    navigate,
    topic?.id,
    topic?.title,
    view,
  ]);

  React.useEffect(() => {
    if (!modalOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [modalOpen]);

  const activeSection =
    sections.find((x) => x.id === sectionId) ||
    sections.find((x) => x.id === topic?.sectionId) ||
    null;

  const createModalSection = sections.find(
    (x) => String(x.id) === String(createForm.sectionId || "")
  );
  const allowAnonymousTopic = Boolean(createModalSection?.allowAnonymousTopics);
  const allowAnonymousComments = Boolean(activeSection?.allowAnonymousComments);

  React.useEffect(() => {
    if (allowAnonymousTopic) return;
    setCreateForm((prev) => ({ ...prev, anonymous: false }));
  }, [allowAnonymousTopic]);

  React.useEffect(() => {
    if (allowAnonymousComments) return;
    setCommentAnonymous(false);
    setReplyAnonymous(false);
  }, [allowAnonymousComments]);

  React.useEffect(() => {
    const shouldCompose = searchParams.get("compose") === "1";
    if (!shouldCompose || booting) return;

    if (!isAuthenticated) {
      redirectToLogin();
      return;
    }

    setCreateForm((prev) => ({
      ...prev,
      sectionId: prev.sectionId || String(sectionId || sections[0]?.id || ""),
    }));
    setModalOpen(true);

    const next = new URLSearchParams(searchParams);
    next.delete("compose");
    setSearchParams(next, { replace: true });
  }, [booting, isAuthenticated, redirectToLogin, searchParams, sectionId, sections, setSearchParams]);

  const sectionsFiltered = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter((item) => {
      const haystack = `${item.title} ${item.description}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [searchQuery, sections]);

  const topicsFiltered = React.useMemo(() => {
    let list = topicsPage.content;

    if (topicsFilter === TOPIC_FILTERS.ACTIVE) {
      list = list.filter((item) => item.commentsCount > 0 || item.reactionsCount > 0);
    } else if (topicsFilter === TOPIC_FILTERS.MINE) {
      list = list.filter((item) => item.mine || (meId && item.author?.id && item.author.id === meId));
    } else if (topicsFilter === TOPIC_FILTERS.UNANSWERED) {
      list = list.filter((item) => item.commentsCount === 0);
    }

    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) => {
      const authorName = item.author?.name || "";
      const text = `${item.title} ${authorName}`.toLowerCase();
      return text.includes(q);
    });
  }, [meId, searchQuery, topicsFilter, topicsPage.content]);

  const myTopicsFiltered = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return myTopicsPage.content;
    return myTopicsPage.content.filter((item) => {
      const text = `${item.title} ${item.sectionTitle}`.toLowerCase();
      return text.includes(q);
    });
  }, [myTopicsPage.content, searchQuery]);

  const myCommentsFiltered = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return myCommentsPage.content;
    return myCommentsPage.content.filter((item) => {
      const text = `${item.content} ${item.topicTitle || ""} ${item.topicId || ""}`.toLowerCase();
      return text.includes(q);
    });
  }, [myCommentsPage.content, searchQuery]);

  const commentsTree = React.useMemo(() => buildCommentsTree(comments), [comments]);

  const forumStats = React.useMemo(() => {
    const topicsCount = sections.reduce((sum, item) => sum + (item.topicsCount || 0), 0);
    const commentsCount =
      view === "topic"
        ? comments.length
        : activeTopics.reduce((sum, item) => sum + item.commentsCount, 0);

    const participants = new Set();
    activeTopics.forEach((item) => {
      if (item.author?.id) participants.add(`u:${item.author.id}`);
      else if (item.author?.name) participants.add(`n:${item.author.name}`);
    });
    comments.forEach((item) => {
      if (item.author?.id) participants.add(`u:${item.author.id}`);
      else if (item.author?.name) participants.add(`n:${item.author.name}`);
    });

    return {
      topicsCount,
      sectionsCount: sections.length,
      commentsCount,
      participantsCount: participants.size,
    };
  }, [activeTopics, comments, sections, view]);

  const activeDiscussions = React.useMemo(() => {
    const source = view === "topic" ? relatedTopics : activeTopics;
    return [...source]
      .sort((a, b) => b.commentsCount + b.reactionsCount - (a.commentsCount + a.reactionsCount))
      .slice(0, 4);
  }, [activeTopics, relatedTopics, view]);

  const latestMyTopic = myTopicsPage.content[0] || null;
  const myTopicsBannerSubtitle = latestMyTopic
    ? `${myTopicsPage.totalElements || 0} активных · последняя активность ${formatRelativeTime(
        latestMyTopic.lastModified || latestMyTopic.createdWhen
      )}`
    : "Публикуйте темы и обсуждайте практические кейсы с коллегами";

  const myActivity = React.useMemo(() => {
    const topicItems = myTopicsPage.content.slice(0, 3).map((item) => ({
      id: `topic-${item.id}`,
      topicId: item.id,
      icon: "topic",
      title: item.title || "Тема",
      meta: `Ваша тема · ${item.commentsCount || 0} ответов`,
      count: item.commentsCount > 0 ? `+${item.commentsCount}` : "",
      when: toTimestamp(item.lastModified || item.createdWhen),
    }));
    const commentItems = myCommentsPage.content.slice(0, 3).map((item) => ({
      id: `comment-${item.id}`,
      topicId: item.topicId,
      icon: "comment",
      title: item.topicTitle || "Комментарий в теме",
      meta: "Ваш комментарий",
      count: item.reactionsCount > 0 ? `+${item.reactionsCount}` : "",
      when: toTimestamp(item.createdWhen),
    }));
    return [...topicItems, ...commentItems].sort((a, b) => b.when - a.when).slice(0, 4);
  }, [myCommentsPage.content, myTopicsPage.content]);

  const notificationsBadgeVisible = React.useMemo(() => {
    const direct = Number(me?.unreadNotifications ?? me?.notificationsUnread ?? me?.unreadCount);
    if (Number.isFinite(direct)) return direct > 0;
    return true;
  }, [me?.notificationsUnread, me?.unreadCount, me?.unreadNotifications]);

  const pagination = React.useMemo(() => {
    const current = topicsPage.number + 1;
    return paginate(topicsPage.totalPages, current);
  }, [topicsPage.number, topicsPage.totalPages]);

  const myTopicsPagination = React.useMemo(() => {
    const current = myTopicsPage.number + 1;
    return paginate(myTopicsPage.totalPages, current);
  }, [myTopicsPage.number, myTopicsPage.totalPages]);

  const myCommentsPagination = React.useMemo(() => {
    const current = myCommentsPage.number + 1;
    return paginate(myCommentsPage.totalPages, current);
  }, [myCommentsPage.number, myCommentsPage.totalPages]);

  const canonicalUrl = React.useMemo(() => {
    if (!origin) return "";
    if (view === "topic" && topicId) return `${origin}${buildTopicPath(topicId, topic?.title || "")}`;
    if (view === "topics" && sectionId) return `${origin}/forum/sections/${sectionId}`;
    if (view === "my-topics") return `${origin}/forum/my/topics`;
    if (view === "my-comments") return `${origin}/forum/my/comments`;
    return `${origin}/forum`;
  }, [origin, sectionId, topic?.title, topicId, view]);

  const openSectionsView = React.useCallback(() => {
    navigate("/forum");
  }, [navigate]);

  const openTopicsView = React.useCallback(
    (targetSectionId) => {
      if (!targetSectionId) return;
      navigate(`/forum/sections/${targetSectionId}`);
    },
    [navigate]
  );

  const openTopicView = React.useCallback(
    (targetTopicId, topicTitle = "") => {
      if (!targetTopicId) return;
      navigate(buildTopicPath(targetTopicId, topicTitle));
    },
    [navigate]
  );

  const openMyTopicsView = React.useCallback(() => {
    navigate("/forum/my/topics");
  }, [navigate]);

  const openMyCommentsView = React.useCallback(() => {
    navigate("/forum/my/comments");
  }, [navigate]);

  const syncTopicInLists = React.useCallback((nextTopic) => {
    setTopicsPage((prev) => ({
      ...prev,
      content: prev.content.map((item) => (item.id === nextTopic.id ? { ...item, ...nextTopic } : item)),
    }));
    setMyTopicsPage((prev) => ({
      ...prev,
      content: prev.content.map((item) => (item.id === nextTopic.id ? { ...item, ...nextTopic } : item)),
    }));
    setActiveTopics((prev) => prev.map((item) => (item.id === nextTopic.id ? { ...item, ...nextTopic } : item)));
    setRelatedTopics((prev) => prev.map((item) => (item.id === nextTopic.id ? { ...item, ...nextTopic } : item)));
  }, []);

  const onToggleTopicReaction = React.useCallback(
    async (event, targetTopicId, reactionKind = DEFAULT_REACTION_KIND) => {
      event.preventDefault();
      event.stopPropagation();
      if (!requireAuth("Только зарегистрированные пользователи могут ставить реакции.")) return;
      const burstOrigin = getReactionBurstOriginFromEvent(event);
      const burst = getReactionBurstPayload(reactionKind, DEFAULT_REACTION_KIND);
      if (burstOrigin) {
        emitReactionBurst({
          ...burstOrigin,
          ...burst,
        });
      }

      try {
        const result = await forumApi.toggleTopicReaction(targetTopicId, reactionKind);
        const myReactionKind = normalizeReactionKind(result?.myReactionKind, null);
        const likedByMe = Boolean(myReactionKind);
        setTopicsPage((prev) => ({
          ...prev,
          content: prev.content.map((item) =>
            item.id === targetTopicId
              ? { ...item, likedByMe, myReactionKind, reactionsCount: result.reactionsCount }
              : item
          ),
        }));
        setMyTopicsPage((prev) => ({
          ...prev,
          content: prev.content.map((item) =>
            item.id === targetTopicId
              ? { ...item, likedByMe, myReactionKind, reactionsCount: result.reactionsCount }
              : item
          ),
        }));
        setActiveTopics((prev) =>
          prev.map((item) =>
            item.id === targetTopicId
              ? { ...item, likedByMe, myReactionKind, reactionsCount: result.reactionsCount }
              : item
          )
        );
        setRelatedTopics((prev) =>
          prev.map((item) =>
            item.id === targetTopicId
              ? { ...item, likedByMe, myReactionKind, reactionsCount: result.reactionsCount }
              : item
          )
        );
        setTopic((prev) =>
          prev && prev.id === targetTopicId
            ? { ...prev, likedByMe, myReactionKind, reactionsCount: result.reactionsCount }
            : prev
        );
      } catch (error) {
        notify("error", mapForumError(error, "Не удалось переключить реакцию."));
      }
    },
    [emitReactionBurst, getReactionBurstOriginFromEvent, notify, requireAuth]
  );

  const onToggleCommentReaction = React.useCallback(
    async (event, commentId, reactionKind = DEFAULT_REACTION_KIND) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (!requireAuth("Только зарегистрированные пользователи могут ставить реакции.")) return;
      const burstOrigin = getReactionBurstOriginFromEvent(event);
      const burst = getReactionBurstPayload(reactionKind, DEFAULT_REACTION_KIND);
      if (burstOrigin) {
        emitReactionBurst({
          ...burstOrigin,
          ...burst,
        });
      }
      try {
        const result = await forumApi.toggleCommentReaction(commentId, reactionKind);
        const myReactionKind = normalizeReactionKind(result?.myReactionKind, null);
        const likedByMe = Boolean(myReactionKind);
        setComments((prev) =>
          prev.map((item) =>
            item.id === commentId
              ? { ...item, likedByMe, myReactionKind, reactionsCount: result.reactionsCount }
              : item
          )
        );
        setMyCommentsPage((prev) => ({
          ...prev,
          content: prev.content.map((item) =>
            item.id === commentId
              ? { ...item, likedByMe, myReactionKind, reactionsCount: result.reactionsCount }
              : item
          ),
        }));
      } catch (error) {
        notify("error", mapForumError(error, "Не удалось переключить реакцию комментария."));
      }
    },
    [emitReactionBurst, getReactionBurstOriginFromEvent, notify, requireAuth]
  );

  const onOpenCreateModal = React.useCallback(() => {
    if (!requireAuth("Только зарегистрированные пользователи могут создавать темы.")) return;
    setCreateForm((prev) => ({
      ...prev,
      sectionId: prev.sectionId || String(sectionId || sections[0]?.id || ""),
    }));
    setModalOpen(true);
  }, [requireAuth, sectionId, sections]);

  const onCreateTopic = React.useCallback(
    async (event) => {
      event.preventDefault();
      if (!requireAuth("Только зарегистрированные пользователи могут создавать темы.")) return;

      const payload = {
        sectionId: parseId(createForm.sectionId),
        title: String(createForm.title || "").trim(),
        content: String(createForm.content || "").trim(),
        anonymous: Boolean(createForm.anonymous && allowAnonymousTopic),
      };

      if (!payload.sectionId) {
        notify("warning", "Выберите раздел.");
        return;
      }
      if (!payload.title) {
        notify("warning", "Укажите заголовок темы.");
        return;
      }
      if (!payload.content) {
        notify("warning", "Добавьте текст темы.");
        return;
      }

      setCreatePending(true);
      try {
        const created = await forumApi.createTopic(payload);
        setModalOpen(false);
        setCreateForm((prev) => ({ ...prev, title: "", content: "", anonymous: false }));
        notify("success", "Тема опубликована.");
        if (created?.sectionId === sectionId) {
          loadTopics(sectionId, 0);
        }
        if (view === "my-topics") {
          loadMyTopics(0);
        }
        loadActiveTopics(sections);
        if (created?.id) {
          openTopicView(created.id, created.title || "");
        }
      } catch (error) {
        notify("error", mapForumError(error, "Не удалось создать тему."));
      } finally {
        setCreatePending(false);
      }
    },
    [
      allowAnonymousTopic,
      createForm.anonymous,
      createForm.content,
      createForm.sectionId,
      createForm.title,
      loadTopics,
      notify,
      loadActiveTopics,
      loadMyTopics,
      openTopicView,
      requireAuth,
      sectionId,
      sections,
      view,
    ]
  );

  const onSubmitComment = React.useCallback(
    async (event) => {
      event.preventDefault();
      if (!topic?.id) return;
      if (!requireAuth("Только зарегистрированные пользователи могут оставлять комментарии.")) return;
      const text = commentText.trim();
      if (!text) {
        notify("warning", "Введите текст комментария.");
        return;
      }
      setCommentPending(true);
      try {
        await forumApi.createComment(topic.id, {
          content: text,
          parentCommentId: null,
          anonymous: Boolean(commentAnonymous && allowAnonymousComments),
        });
        setCommentText("");
        setCommentAnonymous(false);
        await loadComments(topic.id);
        await loadTopic(topic.id, false);
        if (isAuthenticated) {
          await loadMyComments(0);
        }
        notify("success", "Комментарий опубликован.");
      } catch (error) {
        notify("error", mapForumError(error, "Не удалось отправить комментарий."));
      } finally {
        setCommentPending(false);
      }
    },
    [
      allowAnonymousComments,
      commentAnonymous,
      commentText,
      isAuthenticated,
      loadComments,
      loadMyComments,
      loadTopic,
      notify,
      requireAuth,
      topic?.id,
    ]
  );

  const onSubmitReply = React.useCallback(
    async (event) => {
      event.preventDefault();
      if (!topic?.id || !replyToCommentId) return;
      if (!requireAuth("Только зарегистрированные пользователи могут оставлять комментарии.")) return;
      const text = replyText.trim();
      if (!text) {
        notify("warning", "Введите текст ответа.");
        return;
      }
      setReplyPending(true);
      try {
        await forumApi.createComment(topic.id, {
          content: text,
          parentCommentId: replyToCommentId,
          anonymous: Boolean(replyAnonymous && allowAnonymousComments),
        });
        setReplyText("");
        setReplyAnonymous(false);
        setReplyToCommentId(null);
        await loadComments(topic.id);
        await loadTopic(topic.id, false);
        if (isAuthenticated) {
          await loadMyComments(0);
        }
        notify("success", "Ответ опубликован.");
      } catch (error) {
        notify("error", mapForumError(error, "Не удалось отправить ответ."));
      } finally {
        setReplyPending(false);
      }
    },
    [
      allowAnonymousComments,
      isAuthenticated,
      loadComments,
      loadMyComments,
      loadTopic,
      notify,
      replyAnonymous,
      replyText,
      replyToCommentId,
      requireAuth,
      topic?.id,
    ]
  );

  const onTogglePin = React.useCallback(async () => {
    if (!topic?.id || !canModerate) return;
    try {
      const next = await forumApi.setPinned(topic.id, !topic.pinned);
      setTopic(next);
      syncTopicInLists(next);
      notify("success", next.pinned ? "Тема закреплена." : "Тема откреплена.");
    } catch (error) {
      notify("error", mapForumError(error, "Не удалось изменить закрепление."));
    }
  }, [canModerate, notify, syncTopicInLists, topic]);

  const onToggleLock = React.useCallback(async () => {
    if (!topic?.id || !canModerate) return;
    try {
      const next = await forumApi.setLocked(topic.id, !topic.locked);
      setTopic(next);
      syncTopicInLists(next);
      notify("success", next.locked ? "Тема закрыта." : "Тема снова открыта.");
    } catch (error) {
      notify("error", mapForumError(error, "Не удалось изменить статус темы."));
    }
  }, [canModerate, notify, syncTopicInLists, topic]);

  const onCopyTopicLink = React.useCallback(async () => {
    if (!topic?.id || typeof navigator === "undefined" || !navigator.clipboard) return;
    try {
      const url = `${window.location.origin}${buildTopicPath(topic.id, topic.title || "")}`;
      await navigator.clipboard.writeText(url);
      notify("success", "Ссылка на тему скопирована.");
    } catch (error) {
      notify("error", "Не удалось скопировать ссылку.");
    }
  }, [notify, topic?.id, topic?.title]);

  const pageTitle =
    view === "topic"
      ? `${topic?.title || "Тема"} — Форум | Бюро Души`
      : view === "topics"
      ? `${activeSection?.title || "Раздел"} — Форум | Бюро Души`
      : view === "my-topics"
      ? "Мои темы — Форум | Бюро Души"
      : view === "my-comments"
      ? "Мои комментарии — Форум | Бюро Души"
      : "Форум — Бюро Души";

  const renderCommentNode = React.useCallback(
    (node, depth = 0) => {
      const isReply = depth > 0;
      return (
        <React.Fragment key={node.id}>
          <div
            className={`forum-comment${isReply ? " is-reply" : ""}`}
            style={{ "--forum-reply-level": Math.min(depth, 6) }}
          >
            <div className="forum-comment-header">
              <Avatar author={node.author} size="sm" />
              <div className="forum-comment-author-wrap">
                {renderAuthorName(node.author, "forum-comment-author")}
                <span className="forum-comment-time">{formatDateTime(node.createdWhen)}</span>
                {node.author?.anonymous ? <span className="forum-mini-badge">Анонимно</span> : null}
              </div>
            </div>

            <div className="forum-comment-text">{node.content}</div>

            <div className="forum-comment-actions">
              <ReactionSelector
                reactionsCount={node.reactionsCount}
                myReactionKind={node.myReactionKind}
                compact
                onToggle={(event, kind) => onToggleCommentReaction(event, node.id, kind)}
              />
              {!topic?.locked ? (
                <button
                  type="button"
                  className="forum-action-btn"
                  onClick={() => {
                    if (!requireAuth("Только зарегистрированные пользователи могут отвечать.")) return;
                    setReplyToCommentId((prev) => (prev === node.id ? null : node.id));
                    setReplyText("");
                    setReplyAnonymous(false);
                  }}
                >
                  Ответить
                </button>
              ) : null}
              <ReportContentButton
                targetType="FORUM_COMMENT"
                targetId={node.id}
                title={topic?.title || "Комментарий форума"}
                contentSnippet={node.content}
                className="forum-action-btn forum-action-btn--report"
                compact
              />
            </div>

            {replyToCommentId === node.id && !topic?.locked ? (
              <form className="forum-inline-reply" onSubmit={onSubmitReply}>
                <textarea
                  value={replyText}
                  onChange={(event) => setReplyText(event.target.value)}
                  placeholder="Ваш ответ..."
                  rows={3}
                />
                <div className="forum-inline-reply-footer">
                  <div className="forum-emoji-tools">
                    {COMPOSER_EMOJIS.map((emoji) => (
                      <button
                        key={`reply-${node.id}-${emoji}`}
                        type="button"
                        className="forum-emoji-btn"
                        onClick={() => appendEmoji(setReplyText, emoji)}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                  {allowAnonymousComments ? (
                    <label className="forum-check">
                      <input
                        type="checkbox"
                        checked={replyAnonymous}
                        onChange={(event) => setReplyAnonymous(event.target.checked)}
                      />
                      <span>Опубликовать анонимно</span>
                    </label>
                  ) : (
                    <span className="forum-anon-disabled">Анонимные ответы в этом разделе отключены</span>
                  )}
                  <div className="forum-inline-reply-actions">
                    <button
                      type="button"
                      className="forum-btn forum-btn-ghost"
                      onClick={() => {
                        setReplyToCommentId(null);
                        setReplyText("");
                        setReplyAnonymous(false);
                      }}
                    >
                      Отмена
                    </button>
                    <button type="submit" className="forum-btn forum-btn-primary" disabled={replyPending}>
                      {replyPending ? "Отправка..." : "Ответить"}
                    </button>
                  </div>
                </div>
              </form>
            ) : null}
          </div>

          {node.children?.length
            ? node.children.map((child) => renderCommentNode(child, depth + 1))
            : null}
        </React.Fragment>
      );
    },
    [
      allowAnonymousComments,
      appendEmoji,
      onSubmitReply,
      onToggleCommentReaction,
      replyAnonymous,
      replyPending,
      replyText,
      replyToCommentId,
      renderAuthorName,
      requireAuth,
      topic?.locked,
      topic?.title,
    ]
  );

  const forumInner = (
    <>
      <div className={`forum-layout${isAuthenticated ? " forum-layout-auth" : ""}`}>
        <aside className="forum-sidebar">
          <div className="forum-sidebar-group-label">Навигация</div>
          <button
            type="button"
            className={`forum-sidebar-item${view === "sections" ? " active" : ""}`}
            onClick={openSectionsView}
          >
            Все разделы
          </button>
          <button
            type="button"
            className={`forum-sidebar-item${view === "topics" ? " active" : ""}`}
            onClick={() => {
              const target = sectionId || sections[0]?.id;
              if (target) openTopicsView(target);
            }}
          >
            Темы раздела
          </button>
          {isAuthenticated ? (
            <>
              <button
                type="button"
                className={`forum-sidebar-item${view === "my-topics" ? " active" : ""}`}
                onClick={openMyTopicsView}
              >
                <span className="forum-sidebar-item-title">Мои темы</span>
                <span className="forum-sidebar-count">{myTopicsPage.totalElements || 0}</span>
              </button>
              <button
                type="button"
                className={`forum-sidebar-item${view === "my-comments" ? " active" : ""}`}
                onClick={openMyCommentsView}
              >
                <span className="forum-sidebar-item-title">Мои комментарии</span>
                <span className="forum-sidebar-count">{myCommentsPage.totalElements || 0}</span>
              </button>
            </>
          ) : null}

          <div className="forum-sidebar-group-label with-gap">Разделы</div>
          {sections.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`forum-sidebar-item${
                item.id === sectionId || item.id === topic?.sectionId ? " active" : ""
              }`}
              onClick={() => openTopicsView(item.id)}
            >
              <span className="forum-sidebar-item-title">{item.title}</span>
              <span className="forum-sidebar-count">{item.topicsCount}</span>
            </button>
          ))}
        </aside>

        <main className="forum-main">
          <nav className="forum-breadcrumb" aria-label="Хлебные крошки">
            {view === "sections" ? (
              <span className="forum-breadcrumb-item current">
                <span className="forum-breadcrumb-label">Форум</span>
              </span>
            ) : null}
            {view === "topics" ? (
              <>
                <button type="button" className="forum-breadcrumb-item" onClick={openSectionsView}>
                  <span className="forum-breadcrumb-label">Форум</span>
                </button>
                <span className="forum-breadcrumb-sep" aria-hidden="true">
                  ›
                </span>
                <span className="forum-breadcrumb-item current is-truncate" title={activeSection?.title || "Раздел"}>
                  <span className="forum-breadcrumb-label">{activeSection?.title || "Раздел"}</span>
                </span>
              </>
            ) : null}
            {view === "topic" ? (
              <>
                <button type="button" className="forum-breadcrumb-item" onClick={openSectionsView}>
                  <span className="forum-breadcrumb-label">Форум</span>
                </button>
                <span className="forum-breadcrumb-sep" aria-hidden="true">
                  ›
                </span>
                <button
                  type="button"
                  className="forum-breadcrumb-item is-truncate"
                  title={activeSection?.title || "Раздел"}
                  onClick={() => {
                    if (topic?.sectionId) openTopicsView(topic.sectionId);
                  }}
                >
                  <span className="forum-breadcrumb-label">{activeSection?.title || "Раздел"}</span>
                </button>
                <span className="forum-breadcrumb-sep" aria-hidden="true">
                  ›
                </span>
                <span className="forum-breadcrumb-item current is-truncate" title={topic?.title || "Тема"}>
                  <span className="forum-breadcrumb-label">{topic?.title || "Тема"}</span>
                </span>
              </>
            ) : null}
            {view === "my-topics" ? (
              <>
                <button type="button" className="forum-breadcrumb-item" onClick={openSectionsView}>
                  <span className="forum-breadcrumb-label">Форум</span>
                </button>
                <span className="forum-breadcrumb-sep" aria-hidden="true">
                  ›
                </span>
                <span className="forum-breadcrumb-item current">
                  <span className="forum-breadcrumb-label">Мои темы</span>
                </span>
              </>
            ) : null}
            {view === "my-comments" ? (
              <>
                <button type="button" className="forum-breadcrumb-item" onClick={openSectionsView}>
                  <span className="forum-breadcrumb-label">Форум</span>
                </button>
                <span className="forum-breadcrumb-sep" aria-hidden="true">
                  ›
                </span>
                <span className="forum-breadcrumb-item current">
                  <span className="forum-breadcrumb-label">Мои комментарии</span>
                </span>
              </>
            ) : null}
          </nav>

          <div className="forum-page-header">
            <div>
              <h1 className="forum-page-title">
                {view === "sections"
                  ? "Сообщество"
                  : view === "topics"
                  ? activeSection?.title || "Раздел форума"
                  : view === "my-topics"
                  ? "Мои темы"
                  : view === "my-comments"
                  ? "Мои комментарии"
                  : topic?.title || "Тема форума"}
              </h1>
              <p className="forum-page-subtitle">
                {view === "sections"
                  ? "Обсуждайте практику, делитесь опытом и поддерживайте коллег."
                  : view === "topics"
                  ? activeSection?.description || "Темы внутри выбранного раздела."
                  : view === "my-topics"
                  ? "Все темы, которые вы создали."
                  : view === "my-comments"
                  ? "История ваших ответов в обсуждениях."
                  : "Обсуждение темы и комментарии участников."}
              </p>
            </div>

            <button type="button" className="forum-btn forum-btn-primary" onClick={onOpenCreateModal}>
              Создать тему
            </button>
          </div>

          {!isAuthenticated ? (
            <div className="forum-search-row">
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={
                  view === "sections"
                    ? "Поиск по разделам..."
                    : view === "topics"
                    ? "Поиск по темам..."
                    : view === "my-topics"
                    ? "Поиск по вашим темам..."
                    : view === "my-comments"
                    ? "Поиск по вашим комментариям..."
                    : "Поиск по теме..."
                }
              />
            </div>
          ) : null}

          {notice.text ? (
            <div className={`forum-notice ${notice.type || ""}`.trim()}>{notice.text}</div>
          ) : null}

          {view === "sections" ? (
            <section className="forum-sections">
              {sectionsLoading ? <div className="forum-state">Загружаем разделы...</div> : null}
              {sectionsError ? <div className="forum-state forum-state-error">{sectionsError}</div> : null}

              {isAuthenticated ? (
                <div className="forum-my-topics-banner">
                  <div className="forum-my-topics-banner-icon">💬</div>
                  <div className="forum-my-topics-banner-body">
                    <div className="forum-my-topics-banner-title">Ваши темы</div>
                    <div className="forum-my-topics-banner-sub">{myTopicsBannerSubtitle}</div>
                  </div>
                  <div className="forum-my-topics-banner-count">{myTopicsPage.totalElements || 0}</div>
                  <button type="button" className="forum-btn forum-btn-ghost forum-my-topics-banner-btn" onClick={openMyTopicsView}>
                    Посмотреть →
                  </button>
                </div>
              ) : null}

              {!sectionsLoading && !sectionsError && sectionsFiltered.length === 0 ? (
                <div className="forum-empty-state">
                  <div className="forum-empty-title">Ничего не найдено</div>
                  <div className="forum-empty-sub">Попробуйте изменить запрос.</div>
                </div>
              ) : null}

              {sectionsFiltered.map((item) => {
                const latestTopic = sectionLatestTopics[item.id];
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="forum-section-card"
                    onClick={() => openTopicsView(item.id)}
                  >
                    <div className="forum-section-icon">{item.title.slice(0, 1).toUpperCase()}</div>
                    <div className="forum-section-body">
                      <div className="forum-section-name">{item.title}</div>
                      <div className="forum-section-desc">{item.description || "Описание раздела не добавлено"}</div>
                      {latestTopic ? (
                        <div className="forum-section-last">
                          <Avatar author={latestTopic.author} size="sm" />
                          <div className="forum-section-last-text">
                            <div className="forum-section-last-title">{latestTopic.title}</div>
                            <div className="forum-section-last-meta">
                              {renderAuthorName(latestTopic.author)} ·{" "}
                              {formatRelativeTime(latestTopic.lastModified || latestTopic.createdWhen)}
                            </div>
                          </div>
                          {latestTopic.unread ? <span className="forum-topic-badge lav">Новое</span> : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="forum-section-count-wrap">
                      <div className="forum-section-count">{item.topicsCount}</div>
                      <div className="forum-section-count-label">темы</div>
                    </div>
                  </button>
                );
              })}
            </section>
          ) : null}

          {view === "topics" ? (
            <section className="forum-topics">
              <div className="forum-topics-filters">
                <button
                  type="button"
                  className={topicsFilter === TOPIC_FILTERS.ALL ? "active" : ""}
                  onClick={() => setTopicsFilter(TOPIC_FILTERS.ALL)}
                >
                  Все темы
                </button>
                <button
                  type="button"
                  className={topicsFilter === TOPIC_FILTERS.ACTIVE ? "active" : ""}
                  onClick={() => setTopicsFilter(TOPIC_FILTERS.ACTIVE)}
                >
                  Активные
                </button>
                <button
                  type="button"
                  className={topicsFilter === TOPIC_FILTERS.MINE ? "active" : ""}
                  onClick={() => setTopicsFilter(TOPIC_FILTERS.MINE)}
                >
                  Мои темы
                </button>
                <button
                  type="button"
                  className={topicsFilter === TOPIC_FILTERS.UNANSWERED ? "active" : ""}
                  onClick={() => setTopicsFilter(TOPIC_FILTERS.UNANSWERED)}
                >
                  Без ответа
                </button>
                <div className="forum-topics-total">{topicsPage.totalElements} тем</div>
              </div>

              {topicsLoading ? <div className="forum-state">Загружаем темы...</div> : null}
              {topicsError ? <div className="forum-state forum-state-error">{topicsError}</div> : null}

              {!topicsLoading && !topicsError && topicsFiltered.length === 0 ? (
                <div className="forum-empty-state">
                  <div className="forum-empty-title">Темы не найдены</div>
                  <div className="forum-empty-sub">Попробуйте выбрать другой фильтр или создать новую тему.</div>
                </div>
              ) : null}

              {!topicsLoading && !topicsError && topicsFiltered.length ? (
                <div className="forum-topics-list">
                  {topicsFiltered.map((item) => {
                    const isMine = Boolean(item.mine || (meId && item.author?.id && item.author.id === meId));
                    const isUnread = Boolean(item.unread);
                    return (
                      <div
                        key={item.id}
                        className={`forum-topic-row${item.pinned ? " pinned" : ""}${item.locked ? " locked" : ""}${isMine ? " mine" : ""}${isUnread ? " unread" : ""}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => openTopicView(item.id, item.title || "")}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openTopicView(item.id, item.title || "");
                          }
                        }}
                      >
                        <Avatar author={item.author} />
                        <div className="forum-topic-main">
                          <div className="forum-topic-title-row">
                            <span className="forum-topic-title">{item.title}</span>
                            {item.pinned ? <span className="forum-topic-badge mint">Закреплено</span> : null}
                            {isMine ? <span className="forum-topic-badge lav">Ваша тема</span> : null}
                            {item.locked ? <span className="forum-topic-badge grey">Закрыто</span> : null}
                          </div>
                          <div className="forum-topic-meta-row">
                            <span className="forum-topic-author">
                              {renderAuthorName(item.author)}
                              {isMine ? " (вы)" : ""}
                            </span>
                            <span>·</span>
                            <span>{formatRelativeTime(item.lastModified || item.createdWhen) || formatDate(item.createdWhen)}</span>
                            <ReactionSelector
                              reactionsCount={item.reactionsCount}
                              myReactionKind={item.myReactionKind}
                              compact
                              onToggle={(event, kind) => onToggleTopicReaction(event, item.id, kind)}
                            />
                            <ReportContentButton
                              targetType="FORUM_TOPIC"
                              targetId={item.id}
                              title={item.title}
                              contentSnippet={item.content}
                              className="forum-action-btn forum-action-btn--report"
                              compact
                            />
                          </div>
                        </div>
                        <div className="forum-topic-stats">
                          <span>{item.commentsCount} комм.</span>
                          <span>{item.viewsCount} просмотров</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {!topicsLoading && topicsPage.totalPages > 1 ? (
                <div className="forum-pagination">
                  <button
                    type="button"
                    className="forum-page-btn"
                    disabled={topicsPage.first}
                    onClick={() => loadTopics(sectionId, Math.max(0, topicsPage.number - 1))}
                  >
                    ‹
                  </button>
                  {pagination.map((item, index) =>
                    item === "..." ? (
                      <span key={`gap-${index}`} className="forum-page-gap">
                        ...
                      </span>
                    ) : (
                      <button
                        key={`p-${item}`}
                        type="button"
                        className={`forum-page-btn${topicsPage.number + 1 === item ? " active" : ""}`}
                        onClick={() => loadTopics(sectionId, item - 1)}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="forum-page-btn"
                    disabled={topicsPage.last}
                    onClick={() =>
                      loadTopics(sectionId, Math.min(topicsPage.totalPages - 1, topicsPage.number + 1))
                    }
                  >
                    ›
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}

          {view === "my-topics" ? (
            <section className="forum-topics">
              {myTopicsLoading ? <div className="forum-state">Загружаем ваши темы...</div> : null}
              {myTopicsError ? <div className="forum-state forum-state-error">{myTopicsError}</div> : null}

              {!myTopicsLoading && !myTopicsError && myTopicsFiltered.length === 0 ? (
                <div className="forum-empty-state">
                  <div className="forum-empty-title">Вы пока не создали ни одной темы</div>
                  <div className="forum-empty-sub">Создайте обсуждение и оно появится здесь.</div>
                </div>
              ) : null}

              {!myTopicsLoading && !myTopicsError && myTopicsFiltered.length ? (
                <div className="forum-topics-list">
                  {myTopicsFiltered.map((item) => (
                    <div
                      key={item.id}
                      className={`forum-topic-row${item.pinned ? " pinned" : ""}${item.locked ? " locked" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => openTopicView(item.id, item.title || "")}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openTopicView(item.id, item.title || "");
                        }
                      }}
                    >
                      <Avatar author={item.author} />
                      <div className="forum-topic-main">
                        <div className="forum-topic-title-row">
                          <span className="forum-topic-title">{item.title}</span>
                          {item.pinned ? <span className="forum-topic-badge mint">Закреплено</span> : null}
                          {item.locked ? <span className="forum-topic-badge grey">Закрыто</span> : null}
                        </div>
                        <div className="forum-topic-meta-row">
                          <span className="forum-topic-author">{item.sectionTitle || "Раздел форума"}</span>
                          <span>·</span>
                          <span>{formatDate(item.createdWhen)}</span>
                          <ReactionSelector
                            reactionsCount={item.reactionsCount}
                            myReactionKind={item.myReactionKind}
                            compact
                            onToggle={(event, kind) => onToggleTopicReaction(event, item.id, kind)}
                          />
                          <ReportContentButton
                            targetType="FORUM_TOPIC"
                            targetId={item.id}
                            title={item.title}
                            contentSnippet={item.content || item.title}
                            className="forum-action-btn forum-action-btn--report"
                            compact
                          />
                        </div>
                      </div>
                      <div className="forum-topic-stats">
                        <span>{item.commentsCount} комм.</span>
                        <span>{item.viewsCount} просмотров</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {!myTopicsLoading && myTopicsPage.totalPages > 1 ? (
                <div className="forum-pagination">
                  <button
                    type="button"
                    className="forum-page-btn"
                    disabled={myTopicsPage.first}
                    onClick={() => loadMyTopics(Math.max(0, myTopicsPage.number - 1))}
                  >
                    ‹
                  </button>
                  {myTopicsPagination.map((item, index) =>
                    item === "..." ? (
                      <span key={`my-topics-gap-${index}`} className="forum-page-gap">
                        ...
                      </span>
                    ) : (
                      <button
                        key={`my-topics-${item}`}
                        type="button"
                        className={`forum-page-btn${myTopicsPage.number + 1 === item ? " active" : ""}`}
                        onClick={() => loadMyTopics(item - 1)}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="forum-page-btn"
                    disabled={myTopicsPage.last}
                    onClick={() =>
                      loadMyTopics(Math.min(myTopicsPage.totalPages - 1, myTopicsPage.number + 1))
                    }
                  >
                    ›
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}

          {view === "my-comments" ? (
            <section className="forum-topics">
              {myCommentsLoading ? <div className="forum-state">Загружаем ваши комментарии...</div> : null}
              {myCommentsError ? <div className="forum-state forum-state-error">{myCommentsError}</div> : null}

              {!myCommentsLoading && !myCommentsError && myCommentsFiltered.length === 0 ? (
                <div className="forum-empty-state">
                  <div className="forum-empty-title">Вы пока не оставляли комментарии</div>
                  <div className="forum-empty-sub">Когда напишете комментарий, он появится здесь.</div>
                </div>
              ) : null}

              {!myCommentsLoading && !myCommentsError && myCommentsFiltered.length ? (
                <div className="forum-comments-list">
                  {myCommentsFiltered.map((item) => (
                    <div
                      key={item.id}
                      className={`forum-comment forum-my-comment${item.parentCommentId ? " forum-my-comment-reply" : ""}`}
                    >
                      <div className="forum-comment-header">
                        <Avatar author={item.author} size="sm" />
                        <div className="forum-comment-author-wrap">
                          {renderAuthorName(item.author, "forum-comment-author")}
                          <span className="forum-comment-time">{formatDateTime(item.createdWhen)}</span>
                          {item.author?.anonymous ? <span className="forum-mini-badge">Анонимно</span> : null}
                          {item.parentCommentId ? <span className="forum-mini-badge">Ответ</span> : null}
                        </div>
                      </div>
                      <div className="forum-comment-text">{item.content}</div>
                      <div className="forum-comment-actions">
                        {item.topicTitle ? <span className="forum-topic-link-hint">Тема: {item.topicTitle}</span> : null}
                        <ReactionSelector
                          reactionsCount={item.reactionsCount}
                          myReactionKind={item.myReactionKind}
                          compact
                          onToggle={(event, kind) => onToggleCommentReaction(event, item.id, kind)}
                        />
                        <ReportContentButton
                          targetType="FORUM_COMMENT"
                          targetId={item.id}
                          title={item.topicTitle || "Комментарий форума"}
                          contentSnippet={item.content}
                          className="forum-action-btn forum-action-btn--report"
                          compact
                        />
                        <button
                          type="button"
                          className="forum-action-btn"
                          disabled={!item.topicId}
                          onClick={() => openTopicView(item.topicId, item.topicTitle || "")}
                        >
                          Открыть тему #{item.topicId}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {!myCommentsLoading && myCommentsPage.totalPages > 1 ? (
                <div className="forum-pagination">
                  <button
                    type="button"
                    className="forum-page-btn"
                    disabled={myCommentsPage.first}
                    onClick={() => loadMyComments(Math.max(0, myCommentsPage.number - 1))}
                  >
                    ‹
                  </button>
                  {myCommentsPagination.map((item, index) =>
                    item === "..." ? (
                      <span key={`my-comments-gap-${index}`} className="forum-page-gap">
                        ...
                      </span>
                    ) : (
                      <button
                        key={`my-comments-${item}`}
                        type="button"
                        className={`forum-page-btn${myCommentsPage.number + 1 === item ? " active" : ""}`}
                        onClick={() => loadMyComments(item - 1)}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="forum-page-btn"
                    disabled={myCommentsPage.last}
                    onClick={() =>
                      loadMyComments(
                        Math.min(myCommentsPage.totalPages - 1, myCommentsPage.number + 1)
                      )
                    }
                  >
                    ›
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}

          {view === "topic" ? (
            <section className="forum-topic-view">
              {topicLoading ? <div className="forum-state">Загружаем тему...</div> : null}
              {topicError ? <div className="forum-state forum-state-error">{topicError}</div> : null}

              {topic && !topicLoading ? (
                <>
                  {canModerate ? (
                    <div className="forum-mod-bar">
                      <span className="forum-mod-label">Модерация</span>
                      <button
                        type="button"
                        className={`forum-mod-btn${topic.pinned ? " active-pin" : ""}`}
                        onClick={onTogglePin}
                      >
                        {topic.pinned ? "Закреплено" : "Закрепить"}
                      </button>
                      <button
                        type="button"
                        className={`forum-mod-btn${topic.locked ? " active-lock" : ""}`}
                        onClick={onToggleLock}
                      >
                        {topic.locked ? "Закрыто" : "Закрыть"}
                      </button>
                      <button type="button" className="forum-mod-btn" onClick={onCopyTopicLink}>
                        Скопировать ссылку
                      </button>
                    </div>
                  ) : null}

                  <article className="forum-topic-post">
                    <header className="forum-topic-post-header">
                      <div className="forum-topic-post-title-wrap">
                        <h2>{topic.title}</h2>
                        <div className="forum-topic-author-row">
                          <Avatar author={topic.author} />
                          <div>
                            <div className="forum-topic-author-main">
                              {renderAuthorName(topic.author)}
                              {topic.author?.anonymous ? <span className="forum-mini-badge">Анонимно</span> : null}
                            </div>
                            <div className="forum-topic-author-meta">{formatDateTime(topic.createdWhen)}</div>
                          </div>
                        </div>
                      </div>
                    </header>
                    <div className="forum-topic-post-body">{topic.content}</div>
                    <footer className="forum-topic-post-footer">
                      <ReactionSelector
                        reactionsCount={topic.reactionsCount}
                        myReactionKind={topic.myReactionKind}
                        onToggle={(event, kind) => onToggleTopicReaction(event, topic.id, kind)}
                      />
                      <span className="forum-topic-views">{topic.viewsCount} просмотров</span>
                      <ReportContentButton
                        targetType="FORUM_TOPIC"
                        targetId={topic.id}
                        title={topic.title}
                        contentSnippet={topic.content}
                        className="forum-action-btn forum-action-btn--report"
                        compact
                      />
                    </footer>
                  </article>

                  <div className="forum-comments-header">
                    <h3>Комментарии</h3>
                    <span>{comments.length}</span>
                  </div>

                  {topic.locked ? (
                    <div className="forum-locked-banner">
                      Обсуждение закрыто. Новые комментарии не принимаются.
                    </div>
                  ) : null}

                  <div className="forum-comments-list">
                    {commentsLoading ? <div className="forum-state">Загружаем комментарии...</div> : null}
                    {commentsError ? <div className="forum-state forum-state-error">{commentsError}</div> : null}
                    {!commentsLoading && !commentsError && commentsTree.length === 0 ? (
                      <div className="forum-empty-state compact">
                        <div className="forum-empty-title">Комментариев пока нет</div>
                        <div className="forum-empty-sub">Станьте первым участником обсуждения.</div>
                      </div>
                    ) : null}
                    {!commentsLoading && !commentsError && commentsTree.length
                      ? commentsTree.map((item) => renderCommentNode(item, 0))
                      : null}
                  </div>

                  {!topic.locked ? (
                    <form className="forum-add-comment" onSubmit={onSubmitComment}>
                      {isAuthenticated ? (
                        <>
                          <div className="forum-add-comment-header">
                            <Avatar author={{ name: me?.name || me?.email || "Вы" }} size="sm" />
                            <span>Ваш комментарий</span>
                          </div>
                          <textarea
                            value={commentText}
                            onChange={(event) => setCommentText(event.target.value)}
                            rows={4}
                            placeholder="Поделитесь опытом или задайте вопрос..."
                          />
                          <div className="forum-add-comment-footer">
                            <div className="forum-emoji-tools">
                              {COMPOSER_EMOJIS.map((emoji) => (
                                <button
                                  key={`comment-${emoji}`}
                                  type="button"
                                  className="forum-emoji-btn"
                                  onClick={() => appendEmoji(setCommentText, emoji)}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                            {allowAnonymousComments ? (
                              <label className="forum-check">
                                <input
                                  type="checkbox"
                                  checked={commentAnonymous}
                                  onChange={(event) => setCommentAnonymous(event.target.checked)}
                                />
                                <span>Опубликовать анонимно</span>
                              </label>
                            ) : (
                              <span className="forum-anon-disabled">Анонимные комментарии в этом разделе отключены</span>
                            )}
                            <button type="submit" className="forum-btn forum-btn-primary" disabled={commentPending}>
                              {commentPending ? "Отправка..." : "Опубликовать"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="forum-auth-required">
                          Чтобы оставить комментарий, нужно авторизоваться.
                          <button type="button" className="forum-btn forum-btn-primary" onClick={redirectToLogin}>
                            Войти
                          </button>
                        </div>
                      )}
                    </form>
                  ) : null}
                </>
              ) : null}
            </section>
          ) : null}
        </main>

        <aside className="forum-right-panel">
          {isAuthenticated ? (
            <div className="forum-widget">
              <div className="forum-widget-header">
                <span>Моя активность</span>
                <button type="button" className="forum-widget-link" onClick={openMyTopicsView}>
                  Все →
                </button>
              </div>
              <div className="forum-widget-body">
                {myActivity.length === 0 ? (
                  <div className="forum-widget-empty">Пока нет активности.</div>
                ) : (
                  myActivity.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="forum-my-activity-item"
                      onClick={() => {
                        if (item.topicId) openTopicView(item.topicId, item.title || "");
                      }}
                    >
                      <span className={`forum-activity-icon ${item.icon === "comment" ? "is-comment" : "is-topic"}`}>
                        {item.icon === "comment" ? "💬" : "🧵"}
                      </span>
                      <span className="forum-my-activity-text">
                        <span className="forum-my-activity-title">{item.title}</span>
                        <span className="forum-my-activity-meta">{item.meta}</span>
                      </span>
                      {item.count ? <span className="forum-my-activity-count">{item.count}</span> : null}
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : null}

          <div className="forum-widget">
            <div className="forum-widget-header">Статистика форума</div>
            <div className="forum-widget-body">
              <div className="forum-stats-grid">
                <div className="forum-stat-cell">
                  <div className="forum-stat-val mint">{forumStats.topicsCount}</div>
                  <div className="forum-stat-label">темы</div>
                </div>
                <div className="forum-stat-cell">
                  <div className="forum-stat-val lav">{forumStats.commentsCount}</div>
                  <div className="forum-stat-label">комментарии</div>
                </div>
                <div className="forum-stat-cell">
                  <div className="forum-stat-val">{forumStats.participantsCount}</div>
                  <div className="forum-stat-label">участники</div>
                </div>
                <div className="forum-stat-cell">
                  <div className="forum-stat-val forum-stat-val-small">сегодня</div>
                  <div className="forum-stat-label">{activeDiscussions.length} активных</div>
                </div>
              </div>
            </div>
          </div>

          <div className="forum-widget">
            <div className="forum-widget-header">Активные обсуждения</div>
            <div className="forum-widget-body">
              {activeDiscussions.length === 0 ? (
                <div className="forum-widget-empty">
                  {activeTopicsLoading ? "Загружаем обсуждения..." : "Пока нет данных."}
                </div>
              ) : (
                activeDiscussions.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="forum-active-item"
                    onClick={() => openTopicView(item.id, item.title || "")}
                  >
                    <Avatar author={item.author} size="sm" />
                    <div className="forum-active-item-text">
                      <div className="forum-active-item-title">{item.title}</div>
                      <div className="forum-active-item-meta">
                        {item.commentsCount} ответов · {formatRelativeTime(item.lastModified || item.createdWhen)}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="forum-widget">
            <div className="forum-widget-header">Начать обсуждение</div>
            <div className="forum-widget-body">
              <p className="forum-widget-note">
                Создайте новую тему, чтобы получить ответы коллег и обменяться практикой.
              </p>
              <button type="button" className="forum-btn forum-btn-primary full" onClick={onOpenCreateModal}>
                Создать тему
              </button>
            </div>
          </div>
        </aside>
      </div>

      <div className="forum-mobile-nav">
        <button type="button" className={view === "sections" ? "active" : ""} onClick={openSectionsView}>
          Разделы
        </button>
        <button
          type="button"
          className={view === "topics" || view === "my-topics" ? "active" : ""}
          onClick={() => {
            if (isAuthenticated) {
              openMyTopicsView();
              return;
            }
            const target = sectionId || topic?.sectionId || sections[0]?.id;
            if (target) openTopicsView(target);
          }}
        >
          {isAuthenticated ? "Мои темы" : "Темы"}
        </button>
        <button type="button" onClick={onOpenCreateModal}>
          Создать
        </button>
      </div>

      <div className={`forum-modal-overlay${modalOpen ? " open" : ""}`} onClick={(event) => {
        if (event.target === event.currentTarget) setModalOpen(false);
      }}>
        <div className="forum-modal" role="dialog" aria-modal="true" aria-label="Создать тему">
          <div className="forum-modal-header">
            <h3>Новая тема</h3>
            <button type="button" onClick={() => setModalOpen(false)}>
              ✕
            </button>
          </div>
          <form className="forum-modal-body" onSubmit={onCreateTopic}>
            <label className="forum-field">
              <span>Раздел</span>
              <select
                value={createForm.sectionId}
                onChange={(event) =>
                  setCreateForm((prev) => ({ ...prev, sectionId: event.target.value }))
                }
              >
                {sections.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="forum-field">
              <span>Заголовок</span>
              <input
                type="text"
                value={createForm.title}
                onChange={(event) =>
                  setCreateForm((prev) => ({ ...prev, title: event.target.value }))
                }
                placeholder="Кратко и по делу"
                maxLength={180}
              />
            </label>

            <label className="forum-field">
              <span>Содержание</span>
              <textarea
                value={createForm.content}
                onChange={(event) =>
                  setCreateForm((prev) => ({ ...prev, content: event.target.value }))
                }
                placeholder="Опишите вопрос или кейс подробнее"
                rows={7}
              />
            </label>

            {allowAnonymousTopic ? (
              <label className="forum-check">
                <input
                  type="checkbox"
                  checked={createForm.anonymous}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, anonymous: event.target.checked }))
                  }
                />
                <span>Опубликовать анонимно</span>
              </label>
            ) : (
              <div className="forum-anon-disabled">В выбранном разделе анонимные темы отключены</div>
            )}

            {!isAuthenticated ? (
              <div className="forum-auth-required">
                Только зарегистрированные пользователи могут создавать темы.
              </div>
            ) : null}

            <div className="forum-modal-footer">
              <button type="button" className="forum-btn forum-btn-ghost" onClick={() => setModalOpen(false)}>
                Отмена
              </button>
              <button type="submit" className="forum-btn forum-btn-primary" disabled={createPending}>
                {createPending ? "Публикуем..." : "Опубликовать тему"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content="Форум Бюро Души: обсуждения практики, вопросы коллегам и профессиональный обмен опытом."
        />
        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
        <meta name="robots" content="index, follow" />
      </Helmet>

      {isAuthenticated ? (
        <div className="podcasts-hub forum-auth-shell">
          <div className="podcasts-shell">
            <AppSidebarV2
              activeKey="forum"
              mobileOpen={mobileSidebarOpen}
              onCloseMobile={() => setMobileSidebarOpen(false)}
            />

            <button
              className={`podcasts-overlay ${mobileSidebarOpen ? "is-open" : ""}`}
              type="button"
              aria-label="Закрыть меню"
              onClick={() => setMobileSidebarOpen(false)}
            />

            <div className="podcasts-main forum-auth-shell__main">
              <header className="podcasts-topbar forum-auth-shell__topbar">
                <div className="podcasts-topbar__left forum-auth-shell__topbar-left">
                  <button
                    className="podcasts-topbar__menu-btn"
                    type="button"
                    onClick={() => setMobileSidebarOpen(true)}
                    aria-label="Открыть меню"
                  >
                    ☰
                  </button>

                  <h1 className="forum-auth-shell__title">Форум</h1>

                  <label className="forum-auth-shell__search" htmlFor="forum-auth-search">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                      id="forum-auth-search"
                      type="search"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder={
                        view === "sections"
                          ? "Поиск по разделам..."
                          : view === "topics"
                          ? "Поиск по темам..."
                          : view === "my-topics"
                          ? "Поиск по моим темам..."
                          : view === "my-comments"
                          ? "Поиск по моим комментариям..."
                          : "Поиск в обсуждении..."
                      }
                    />
                  </label>
                </div>

                <div className="podcasts-topbar__right forum-auth-shell__topbar-right">
                  <button type="button" className="forum-btn forum-btn-primary forum-auth-shell__create" onClick={onOpenCreateModal}>
                    Новая тема
                  </button>
                  <button
                    type="button"
                    className="forum-auth-shell__icon-btn"
                    onClick={() => navigate("/notifications")}
                    aria-label="Уведомления"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                    {notificationsBadgeVisible ? <span className="forum-auth-shell__notif-dot" /> : null}
                  </button>
                  <button
                    className="podcasts-topbar__avatar"
                    type="button"
                    onClick={() => navigate("/dashboard/settings")}
                    aria-label="Открыть профиль"
                  >
                    {initials(me?.name || me?.fullName || me?.login || me?.email || "Вы")}
                  </button>
                </div>
              </header>

              <div className="forum-page forum-page--embedded forum-page--auth">{forumInner}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="forum-page">{forumInner}</div>
      )}

      <ReactionParticlesLayer particles={reactionParticles} />
    </>
  );
}
